// services/socialService.js
//
// The app side of social sharing / import. The real work happens in the PCake social worker on
// the OneAdmin VPS (social-worker/): it drives a real browser, keeps each login in its own
// browser profile, and talks to the app only through these Firestore collections (always the
// Prod project, which is where the worker listens):
//
//   socialTasks      tasks the app queues: connect | post | import | index | disconnect
//   socialSessions   live view: the worker writes the page picture, the app sends clicks/keys
//   socialAccounts   what is connected (account / Page / chat name, last used)
//   socialSettings   per user: lastPlatform (one-click share), lastImportPlatform
//   socialWorker     heartbeat (is the worker online?)

import {
  addDoc, collection, doc, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where,
} from 'firebase/firestore';
import { getProdDb } from './firebase';

export const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', short: 'IG', color: '#d62976', needsTarget: false },
  { id: 'facebook', label: 'Facebook Page', short: 'FB', color: '#1877f2', needsTarget: true },
  { id: 'whatsapp', label: 'WhatsApp', short: 'WA', color: '#25d366', needsTarget: true },
];
export const platformMeta = (id) => PLATFORMS.find((p) => p.id === id) || null;

const db = () => getProdDb();
let inputSeq = 0;

/**
 * The person's screen, so the worker renders the live view at that size (1:1, readable) — and
 * as a phone on a phone, where sites then show their mobile login pages.
 */
export function liveViewport() {
  if (typeof window === 'undefined') return null;
  const coarse = window.matchMedia?.('(pointer: coarse)').matches;
  const mobile = window.innerWidth < 760 || (coarse && window.innerWidth < 1100);
  return {
    mobile,
    // The live view's picture area: full width on a phone, the dialog's width on a desktop.
    width: Math.round(mobile ? window.innerWidth : Math.min(window.innerWidth - 48, 1180)),
    height: Math.round(Math.max(480, window.innerHeight - (mobile ? 150 : 190))),
    dpr: Math.min(2, window.devicePixelRatio || 1),
  };
}

export async function queueTask(uid, kind, platform, payload = {}) {
  const ref = await addDoc(collection(db(), 'socialTasks'), {
    uid, kind, platform: platform || null, payload: { ...payload, viewport: liveViewport() }, status: 'queued',
    message: 'Waiting for the social worker', createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export const cancelTask = (taskId) =>
  updateDoc(doc(db(), 'socialTasks', taskId), { status: 'cancelled', updatedAt: serverTimestamp() });

export const watchTask = (taskId, cb) =>
  onSnapshot(doc(db(), 'socialTasks', taskId), (s) => cb(s.exists() ? { id: s.id, ...s.data() } : null), () => cb(null));

/** Tasks of this user that are not finished yet (queued / running / live). */
export const watchOpenTasks = (uid, cb) =>
  onSnapshot(
    query(collection(db(), 'socialTasks'), where('uid', '==', uid), where('status', 'in', ['queued', 'running', 'live'])),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => { console.warn('socialTasks listener:', err.message); cb([]); },
  );

export const watchAccounts = (uid, cb) =>
  onSnapshot(
    query(collection(db(), 'socialAccounts'), where('uid', '==', uid)),
    (snap) => cb(Object.fromEntries(snap.docs.map((d) => [d.data().platform, { id: d.id, ...d.data() }]))),
    (err) => { console.warn('socialAccounts listener:', err.message); cb({}); },
  );

export const watchSettings = (uid, cb) =>
  onSnapshot(doc(db(), 'socialSettings', uid), (s) => cb(s.exists() ? s.data() : {}), () => cb({}));

export const saveSettings = (uid, patch) =>
  setDoc(doc(db(), 'socialSettings', uid), { uid, ...patch, updatedAt: serverTimestamp() }, { merge: true });

/** Worker heartbeat: online when it beat within the last 2 minutes. */
export const watchWorker = (cb) =>
  onSnapshot(doc(db(), 'socialWorker', 'status'), (s) => {
    const d = s.exists() ? s.data() : null;
    const beat = d?.beatMs || 0;
    cb({ online: !!d?.online && Date.now() - beat < 120_000, ...d });
  }, () => cb({ online: false }));

export const watchSession = (sessionId, cb) =>
  onSnapshot(doc(db(), 'socialSessions', sessionId), (s) => cb(s.exists() ? { id: s.id, ...s.data() } : null), () => cb(null));

export const setSessionStatus = (sessionId, status) =>
  updateDoc(doc(db(), 'socialSessions', sessionId), { status, updatedAt: serverTimestamp() });

export const sendInput = (uid, sessionId, kind, extra = {}) =>
  addDoc(collection(db(), 'socialSessions', sessionId, 'input'), {
    uid, kind, x: extra.x ?? null, y: extra.y ?? null, text: extra.text ?? null,
    seq: Date.now() * 1000 + (inputSeq++ % 1000),
  });

/** The default post text for a profile — the worker builds the same one when none is sent. */
export function defaultCaption(p) {
  const bits = [];
  const title = (p.theme || '').trim();
  if (title) bits.push(`🎂 ${title}`);
  const flavour = p.cflavour || p.tiers?.[0]?.cflavour;
  const filling = p.cfilling || p.tiers?.[0]?.cfilling;
  const sponge = p.sponge || p.tiers?.[0]?.sponge;
  const line = [flavour && `${flavour} flavour`, filling && `${filling} filling`, sponge && `${sponge} sponge`].filter(Boolean).join(' · ');
  if (line) bits.push(line);
  if (p.cover) bits.push(`Covered in ${p.cover}${p.frosting ? ` with ${p.frosting}` : ''}`);
  const events = Array.isArray(p.cevent) ? p.cevent : [];
  const tags = ['#cake', '#customcake', ...events.map((e) => `#${String(e).replace(/[^a-zA-Z0-9]/g, '')}`)].filter((t) => t.length > 1);
  bits.push('', tags.join(' '));
  return bits.join('\n').trim();
}
